import type { ServiceDefinition } from "../../../modularity/serviceDefinition";
import type { ISceneContext } from "../../sceneContext";
import type { ISceneExplorerService } from "./sceneExplorerService";

import { DropRegular, EditRegular } from "@fluentui/react-icons";

import { Observable } from "core/Misc/observable";
import { ParticleSystem } from "core/Particles/particleSystem";
import { InterceptProperty } from "../../../instrumentation/propertyInstrumentation";
import { EditParticleSystem } from "../../../misc/nodeParticleEditor";
import { SceneContextIdentity } from "../../sceneContext";
import { DefaultCommandsOrder, DefaultSectionsOrder } from "./defaultSectionsMetadata";
import { SceneExplorerServiceIdentity } from "./sceneExplorerService";

export const ParticleSystemExplorerServiceDefinition: ServiceDefinition<[], [ISceneExplorerService, ISceneContext]> = {
    friendlyName: "Particle System Explorer",
    consumes: [SceneExplorerServiceIdentity, SceneContextIdentity],
    factory: (sceneExplorerService, sceneContext) => {
        const scene = sceneContext.currentScene;
        if (!scene) {
            return undefined;
        }

        const sectionRegistration = sceneExplorerService.addSection({
            displayName: "Particle Systems",
            order: DefaultSectionsOrder.ParticleSystems,
            getRootEntities: () => scene.particleSystems,
            getEntityDisplayInfo: (particleSystem) => {
                const onChangeObservable = new Observable<void>();

                const nameHookToken = InterceptProperty(particleSystem, "name", {
                    afterSet: () => {
                        onChangeObservable.notifyObservers();
                    },
                });

                return {
                    get name() {
                        return particleSystem.name;
                    },
                    onChange: onChangeObservable,
                    dispose: () => {
                        nameHookToken.dispose();
                        onChangeObservable.clear();
                    },
                };
            },
            entityIcon: () => <DropRegular />,
            getEntityAddedObservables: () => [scene.onNewParticleSystemAddedObservable],
            getEntityRemovedObservables: () => [scene.onParticleSystemRemovedObservable],
        });

        const editParticleSystemCommandRegistration = sceneExplorerService.addEntityCommand({
            predicate: (entity): entity is ParticleSystem => entity instanceof ParticleSystem && entity.isNodeGenerated,
            order: DefaultCommandsOrder.EditParticleSystem,
            getCommand: (particleSystem) => {
                return {
                    type: "action",
                    displayName: "Edit in Node Particle System Editor",
                    icon: () => <EditRegular />,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    execute: async () => await EditParticleSystem(particleSystem),
                };
            },
        });

        return {
            dispose: () => {
                sectionRegistration.dispose();
                editParticleSystemCommandRegistration.dispose();
            },
        };
    },
};
